import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { SosButton } from "@/components/SosButton";
import { PatrolMap } from "@/components/PatrolMap";
import { HandsFreeVoiceCard } from "@/components/voice/HandsFreeVoiceCard";
import { useUnreadBadges } from "@/hooks/useUnreadBadges";
import { trackCta } from "@/lib/app-analytics";
import { usePrefetchScreens } from "@/lib/screen-prefetch";
import {
  Building2, Users, Briefcase, AlertTriangle, ChevronRight, Clock, Radio,
  ClipboardList, MapPin, QrCode, MessageSquare, Sparkles, X,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/home")({
  component: HomePage,
  head: () => ({ meta: [{ title: "Home — MissionReady" }] }),
});

type Counts = { sites: number; onDuty: number; openShifts: number; activeAlerts: number };
type NewSince = { incidents: number; tours: number };

const LAST_HOME_VISIT_KEY = "mr_last_home_visit";

function HomePage() {
  const { currentOrg, user } = useAuth();
  const [c, setC] = useState<Counts>({ sites: 0, onDuty: 0, openShifts: 0, activeAlerts: 0 });
  const [newSince, setNewSince] = useState<NewSince>({ incidents: 0, tours: 0 });
  const [showWelcome, setShowWelcome] = useState(false);
  const badges = useUnreadBadges();

  // Warm the data for the screens the stats and Quick Access rows point at,
  // so those pages are already populated when tapped.
  usePrefetchScreens(["/time-clock", "/sites", "/incidents", "/schedule", "/team"]);

  useEffect(() => {
    if (!currentOrg) return;
    try {
      const key = `mr_just_onboarded:${currentOrg.id}`;
      if (localStorage.getItem(key) === "1") setShowWelcome(true);
    } catch { /* ignore */ }
  }, [currentOrg?.id]);

  const dismissWelcome = () => {
    setShowWelcome(false);
    if (currentOrg) {
      try { localStorage.removeItem(`mr_just_onboarded:${currentOrg.id}`); } catch { /* ignore */ }
    }
  };

  useEffect(() => {
    if (!currentOrg) return;
    const orgId = currentOrg.id;
    // Read the previous visit timestamp first so we count "new since you were here",
    // then immediately bump the marker so the next mount measures from now.
    let lastVisit: string | null = null;
    try {
      lastVisit = localStorage.getItem(`${LAST_HOME_VISIT_KEY}:${orgId}`);
      localStorage.setItem(`${LAST_HOME_VISIT_KEY}:${orgId}`, new Date().toISOString());
    } catch { /* ignore */ }

    (async () => {
      const [sites, shifts, openShifts, alerts] = await Promise.all([
        supabase.from("sites").select("*", { count: "exact", head: true }).eq("organization_id", orgId).eq("active", true),
        supabase.from("shifts").select("*", { count: "exact", head: true }).eq("organization_id", orgId).eq("status", "active"),
        supabase.from("shifts").select("*", { count: "exact", head: true }).eq("organization_id", orgId).is("officer_id", null).eq("status", "scheduled"),
        supabase.from("incidents").select("*", { count: "exact", head: true }).eq("organization_id", orgId).in("status", ["open", "in_progress"]),
      ]);
      setC({
        sites: sites.count ?? 0,
        onDuty: shifts.count ?? 0,
        openShifts: openShifts.count ?? 0,
        activeAlerts: alerts.count ?? 0,
      });

      if (lastVisit) {
        const [newIncidents, newTours] = await Promise.all([
          supabase.from("incidents").select("*", { count: "exact", head: true })
            .eq("organization_id", orgId).gt("created_at", lastVisit),
          supabase.from("patrols").select("*", { count: "exact", head: true })
            .eq("organization_id", orgId).eq("status", "completed").gt("ended_at", lastVisit),
        ]);
        setNewSince({ incidents: newIncidents.count ?? 0, tours: newTours.count ?? 0 });
      }
    })();
  }, [currentOrg?.id]);

  const greeting =
    (user?.user_metadata as { full_name?: string; name?: string } | undefined)?.full_name ??
    (user?.user_metadata as { name?: string } | undefined)?.name ??
    user?.email?.split("@")[0] ??
    "there";

  return (
    <div className="px-4 py-5 md:px-6 md:py-7">
      <div className="mb-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          {currentOrg?.name ?? "MissionReady"}
        </p>
        <h1 className="font-display text-2xl font-bold md:text-3xl">Hello, {greeting}</h1>
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Field Ops · Dashboard</p>
      </div>

      {showWelcome && (
        <div
          className="mb-5 flex items-start gap-3 rounded-2xl border border-primary/40 p-4 shadow-elegant"
          style={{ background: "linear-gradient(135deg, color-mix(in oklab, var(--primary) 18%, var(--card)), var(--card))" }}
          role="status"
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-base font-semibold">You're all set 🎉</div>
            <p className="text-sm text-muted-foreground">
              Your workspace is live. Invites are on their way and your first site is ready to staff.
            </p>
          </div>
          <button
            type="button"
            onClick={dismissWelcome}
            aria-label="Dismiss"
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* SOS */}
      <div className="mb-6">
        <SosButton />
      </div>

      {/* Hands-Free Voice (MissionReady Voice) — dismissible, optional */}
      <HandsFreeVoiceCard />

      {/* "New since you were here" — quick orient for returning users */}
      {(newSince.incidents > 0 || newSince.tours > 0 || badges.messages > 0) && (
        <Section title="New since you were here">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <NewBadge to="/incidents" count={newSince.incidents} label="new incidents" icon={AlertTriangle} tone="danger" onClick={() => trackCta("home_new_incidents", { count: newSince.incidents })} />
            <NewBadge to="/messages" count={badges.messages} label="unread messages" icon={MessageSquare} tone="primary" onClick={() => trackCta("home_unread_messages", { count: badges.messages })} />
            <NewBadge to="/guard-tours" count={newSince.tours} label="guard tours completed" icon={QrCode} tone="success" onClick={() => trackCta("home_new_tours", { count: newSince.tours })} />
          </div>
        </Section>
      )}

      {/* Overview */}
      <Section title="Overview">
        <div className="grid grid-cols-2 gap-3">
          <Stat to="/sites" label="Active Sites" value={c.sites} icon={Building2} tone="default" />
          <Stat to="/team" label="Guards on Duty" value={c.onDuty} icon={Users} tone="success" />
          <Stat to="/schedule" label="Open Shifts" value={c.openShifts} icon={Briefcase} tone="warning" />
          <Stat to="/incidents" label="Active Alerts" value={c.activeAlerts} icon={AlertTriangle} tone="danger" />
        </div>
      </Section>

      {/* Live Operations */}
      <Section title="Live Operations">
        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          <div className="relative">
            <span className="absolute left-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-primary px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-primary-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-white" /> Live Data
            </span>
            <PatrolMap
              geofences={[]}
              height={220}
              fitTo="geofences"
              showUserLocation
              intelligenceClassName="absolute left-3 top-12 z-[400] inline-flex min-h-9 items-center gap-1.5 rounded-md border border-[#C0C0C0]/40 bg-card/90 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#C0C0C0] backdrop-blur transition-colors hover:bg-[#222]"
            />
          </div>
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <div className="font-display text-base font-semibold">Live Map</div>
              <div className="text-xs text-muted-foreground">Real-time officer GPS · perimeter status</div>
            </div>
            <Link
              to="/operations/live"
              preload="viewport"
              className="inline-flex items-center gap-1 rounded-lg bg-foreground px-3 py-2 text-xs font-bold uppercase tracking-wider text-background"
            >
              Open <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </Section>

      {/* Quick Access */}
      <Section title="Quick Access">
        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          <QuickRow to="/time-clock" icon={Clock} label="Timeclock" />
          <QuickRow to="/guard-tours" icon={QrCode} label="Guard Tours" />
          <QuickRow to="/console/dispatch" icon={Radio} label="Dispatch" />
          <QuickRow to="/reports" icon={ClipboardList} label="Reports" />
          <QuickRow to="/sites" icon={MapPin} label="Sites" />
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-6">
      <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{title}</h2>
      {children}
    </section>
  );
}

function Stat({
  label, value, icon: Icon, tone, to,
}: { label: string; value: number; icon: any; tone: "default" | "success" | "warning" | "danger"; to?: string }) {
  const toneCls =
    tone === "success" ? "bg-success text-success-foreground" :
    tone === "warning" ? "bg-warning text-warning-foreground" :
    tone === "danger"  ? "bg-destructive text-destructive-foreground" :
                         "bg-foreground text-background";
  const body = (
    <>
      <div className="flex items-start justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{label}</span>
        <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${toneCls}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="mt-3 font-display text-3xl font-bold">{value}</div>
    </>
  );
  const cls = "block rounded-2xl border border-border bg-card p-4";
  if (!to) return <div className={cls}>{body}</div>;
  return (
    <Link to={to as never} preload="viewport" className={`${cls} transition-colors hover:border-primary/50 hover:bg-muted/30 active:bg-muted/50`}>
      {body}
    </Link>
  );
}

function QuickRow({ to, icon: Icon, label }: { to: string; icon: any; label: string }) {
  return (
    <Link
      to={to as never}
      preload="viewport"
      className="flex items-center justify-between gap-3 border-b border-border px-4 py-3.5 last:border-b-0 hover:bg-muted/40"
    >
      <div className="flex items-center gap-3">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="text-base font-medium">{label}</span>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </Link>
  );
}

function NewBadge({
  to, count, label, icon: Icon, tone, onClick,
}: {
  to: string; count: number; label: string; icon: any;
  tone: "primary" | "success" | "danger"; onClick?: () => void;
}) {
  if (!count) return null;
  const toneCls =
    tone === "success" ? "bg-success/15 text-success border-success/40" :
    tone === "danger"  ? "bg-destructive/15 text-destructive border-destructive/40" :
                         "bg-primary/15 text-primary border-primary/40";
  return (
    <Link
      to={to as never}
      preload="viewport"
      onClick={onClick}
      className={`flex items-center gap-3 rounded-2xl border p-3 transition-all hover:shadow-card ${toneCls}`}
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-background/60">
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="font-display text-xl font-bold leading-none">{count}</div>
        <div className="mt-0.5 text-xs font-medium">{label}</div>
      </div>
      <ChevronRight className="h-4 w-4 opacity-70" />
    </Link>
  );
}
